import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Typography,
  Divider,
  useTheme,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Stack,
  Fade,
  Zoom,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  AssignmentReturn as ReturnIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Business as BusinessIcon,
  PersonOutline as PersonIcon,
  Warehouse as WarehouseIcon,
  CalendarToday as CalendarIcon,
  Notes as NotesIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import {
  PurchaseReturn,
  CreatePurchaseReturnDto,
  UpdatePurchaseReturnDto,
} from '../../types/purchaseReturns.types';
import { purchaseReturnsService } from '../../services/purchaseReturns.service';
import suppliersService from '../../services/suppliers.service';
import { custodiesService } from '../../services/custodies.service';
import axiosInstance from '../../services/axiosInstance';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';
import { warehousesService } from '../../services/warehouses.service';
import { itemsService, itemUnitsService, unitsService } from '../../services/items.service';
import { itemSuppliersService } from '../../services/itemSuppliers.service';
import { purchaseInvoicesService } from '../../services/purchaseInvoices.service';
import { Supplier } from '../../types/suppliers.types';
import { Custody } from '../../types/custodies.types';
import { Warehouse } from '../../types/warehouses.types';
import { Item } from '../../types/items.types';
import { PurchaseInvoice } from '../../types/purchaseInvoices.types';

const translations = {
  en: {
    addPurchaseReturn: 'Add Purchase Return',
    editPurchaseReturn: 'Edit Purchase Return',
    basicInfo: 'Basic Information',
    paymentInfo: 'Payment Information',
    returnDetails: 'Return Details',
    returnNumber: 'Return Number',
    returnDate: 'Return Date',
    paymentMethod: 'Payment Method',
    purchaseInvoice: 'Purchase Invoice',
    supplier: 'Supplier',
    custody: 'Employee Custody',
    warehouse: 'Warehouse',
    returnReason: 'Return Reason',
    item: 'Item',
    unit: 'Unit',
    quantity: 'Quantity',
    unitPrice: 'Unit Price',
    totalPrice: 'Total',
    lineNotes: 'Line Notes',
    addLine: 'Add Line',
    save: 'Save',
    cancel: 'Cancel',
    required: 'Required',
    optional: 'Optional',
    notes: 'Notes',
    totalAmount: 'Total Amount',
    selectPaymentMethod: 'Select Payment Method',
    selectSupplier: 'Select Supplier',
    selectCustody: 'Select Custody',
    selectWarehouse: 'Select Warehouse',
    selectInvoice: 'Select Invoice (Optional)',
    selectItem: 'Select Item',
    methodSupplier: 'Supplier',
    methodCustody: 'Custody',
    methodPaymentAccount: 'Payment Account',
    payee: 'Payee',
    noInvoice: 'No Invoice',
    paymentSourceRequired: 'Please select payment method',
    saved: 'Purchase return saved successfully',
    updated: 'Purchase return updated successfully',
  },
  ar: {
    addPurchaseReturn: 'إضافة مرتجع مشتريات',
    editPurchaseReturn: 'تعديل مرتجع مشتريات',
    basicInfo: 'المعلومات الأساسية',
    paymentInfo: 'معلومات الدفع',
    returnDetails: 'تفاصيل المرتجع',
    returnNumber: 'رقم المرتجع',
    returnDate: 'تاريخ المرتجع',
    paymentMethod: 'طريقة الدفع',
    purchaseInvoice: 'فاتورة المشتريات',
    supplier: 'المورد',
    custody: 'عهدة موظف',
    warehouse: 'المخزن',
    returnReason: 'سبب الإرجاع',
    item: 'الصنف',
    unit: 'الوحدة',
    quantity: 'الكمية',
    unitPrice: 'سعر الوحدة',
    totalPrice: 'الإجمالي',
    lineNotes: 'ملاحظات السطر',
    addLine: 'إضافة سطر',
    save: 'حفظ',
    cancel: 'إلغاء',
    required: 'مطلوب',
    optional: 'اختياري',
    notes: 'ملاحظات',
    totalAmount: 'الإجمالي الكلي',
    selectPaymentMethod: 'اختر طريقة الدفع',
    selectSupplier: 'اختر المورد',
    selectCustody: 'اختر العهدة',
    selectWarehouse: 'اختر المخزن',
    selectInvoice: 'اختر الفاتورة (اختياري)',
    selectItem: 'اختر الصنف',
    methodSupplier: 'مورد',
    methodCustody: 'عهدة',
    methodPaymentAccount: 'حساب',
    payee: 'المدفوع له',
    noInvoice: 'بدون فاتورة',
    paymentSourceRequired: 'يجب اختيار طريقة الدفع',
    saved: 'تم حفظ مرتجع المشتريات بنجاح',
    updated: 'تم تحديث مرتجع المشتريات بنجاح',
  },
};

interface PurchaseReturnDialogProps {
  open: boolean;
  onClose: () => void;
  purchaseReturn?: PurchaseReturn;
  onSave: () => void;
}

interface DetailLine {
  id: string;
  itemId: number | null;
  item: Item | null;
  unitId: number | null;
  quantity: number;
  unitPrice: number;
  notes: string;
}

const PurchaseReturnDialog: React.FC<PurchaseReturnDialogProps> = ({
  open,
  onClose,
  purchaseReturn,
  onSave,
}) => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { minDate, maxDate } = useFiscalDateRange();
  const isSuperAdmin = useSelector((state: RootState) => state.auth.user?.isSuperAdmin) ?? false;
  const isEditMode = !!purchaseReturn;
  const dateLocked = isEditMode && !isSuperAdmin;

  const gridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(gridRef);

  const [formData, setFormData] = useState({
    returnNumber: '',
    returnDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'Supplier',
    purchaseInvoiceId: null as number | null,
    supplierId: null as number | null,
    custodyId: null as number | null,
    paymentAccountId: null as number | null, // ✅ NEW
    warehouseId: null as number | null,
    returnReason: '',
    notes: '',
  });

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [custodies, setCustodies] = useState<Custody[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [purchaseInvoices, setPurchaseInvoices] = useState<PurchaseInvoice[]>([]);

  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [selectedCustody, setSelectedCustody] = useState<Custody | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<PurchaseInvoice | null>(null);
  const [payeeName, setPayeeName] = useState(''); // ✅ NEW: Store payee name for display
  const [isBranchManager, setIsBranchManager] = useState(false);
  const [isWarehouseKeeper, setIsWarehouseKeeper] = useState(false);

  const [detailLines, setDetailLines] = useState<DetailLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      loadMasterData();
    }
  }, [open]);

  useEffect(() => {
    if (open && suppliers.length > 0 && warehouses.length > 0 && items.length > 0) {
      if (purchaseReturn) {
        loadPurchaseReturn();
      } else {
        loadNewReturnNumber();
        addNewLine();
      }
    }
  }, [open, purchaseReturn, suppliers, custodies, warehouses, items]);

  const loadMasterData = async () => {
    try {
      const [suppliersData, custodiesRes, warehousesData, itemsData, invoicesData] = await Promise.all([
  suppliersService.getAll(),
  axiosInstance.get('/Custodies/purchase-lookup'),
  warehousesService.getAll(),
  itemsService.getAll(),
  purchaseInvoicesService.getAll(),
]);
const custodiesData = custodiesRes.data;
      setSuppliers(suppliersData);
      
      // Read user branches from JWT token
      const token = localStorage.getItem('token');
      let userBranchIds: number[] = [];
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
          const allowedBranches = payload.AllowedBranchId
            ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
            : [];
          userBranchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));
        } catch (e) { console.error('Failed to parse token:', e); }
      }
      setIsBranchManager(userBranchIds.length > 0);

      // Read keeper warehouse IDs from JWT token
      let userWarehouseIds: number[] = [];
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const keeperClaims = payload.KeeperWarehouseId;
          userWarehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
        } catch (e) { console.error('Failed to parse keeper warehouses:', e); }
      }
      setIsWarehouseKeeper(userWarehouseIds.length > 0);
      
      setCustodies(custodiesData);
      
      // Filter warehouses: by keeper (warehouse keeper) or by branch (branch manager) or all
      const filteredWarehouses = userWarehouseIds.length > 0
        ? warehousesData.filter((w: Warehouse) => userWarehouseIds.includes(w.warehouseId))
        : userBranchIds.length === 0
          ? warehousesData
          : warehousesData.filter((w: Warehouse) => 
              w.branchId && userBranchIds.includes(w.branchId)
            );
      setWarehouses(filteredWarehouses);
      
      setItems(itemsData);
      setPurchaseInvoices(invoicesData.filter((inv: any) => inv.status === 'Posted'));
    } catch (error) {
      console.error('Failed to load master data:', error);
      toast.error(language === 'ar' ? 'فشل تحميل البيانات' : 'Failed to load data');
    }
  };

  const loadPurchaseReturn = async () => {
    if (!purchaseReturn) return;

    setFormData({
      returnNumber: purchaseReturn.returnNumber,
      returnDate: purchaseReturn.returnDate.split('T')[0],
      paymentMethod: purchaseReturn.paymentMethod,
      purchaseInvoiceId: purchaseReturn.purchaseInvoiceId || null,
      supplierId: purchaseReturn.supplierId || null,
      custodyId: purchaseReturn.custodyId || null,
      paymentAccountId: purchaseReturn.paymentAccountId || null, // ✅ NEW
      warehouseId: purchaseReturn.warehouseId,
      returnReason: purchaseReturn.returnReason || '',
      notes: purchaseReturn.notes || '',
    });

    const supplier = suppliers.find(s => s.supplierId === purchaseReturn.supplierId);
    const custody = custodies.find(c => c.custodyId === purchaseReturn.custodyId);
    const warehouse = warehouses.find(w => w.warehouseId === purchaseReturn.warehouseId);
    const invoice = purchaseInvoices.find(i => i.purchaseInvoiceId === purchaseReturn.purchaseInvoiceId);

    setSelectedSupplier(supplier || null);
    setSelectedCustody(custody || null);
    setSelectedWarehouse(warehouse || null);
    setSelectedInvoice(invoice || null);

    // ✅ Load units for each detail item
    const linesPromises = purchaseReturn.details.map(async (detail) => {
      // Load units for this item
      let units = await itemUnitsService.getItemUnits(detail.itemId);
      
      // Check if base unit exists
      const item = items.find(i => i.itemId === detail.itemId);
      if (item) {
        const hasBaseUnit = units.some(u => u.unitId === item.baseUnitId);
        
        if (!hasBaseUnit && item.baseUnitId) {
          const allUnits = await unitsService.getAll();
          const baseUnit = allUnits.find(u => u.unitId === item.baseUnitId);
          
          if (baseUnit) {
            const baseUnitAsItemUnit = {
              itemUnitId: 0,
              itemId: item.itemId,
              unitId: baseUnit.unitId,
              unitName: baseUnit.unitName,
              unitNameAr: baseUnit.unitNameAr,
              unitCode: baseUnit.unitCode,
              conversionFactor: 1,
              defaultSalePrice: 0,
              defaultPurchasePrice: 0,
              isDefaultSale: true,
              isDefaultPurchase: true,
            };
            units = [baseUnitAsItemUnit, ...units];
          }
        }
      }

      return {
        id: `${detail.returnDetailId}`,
        itemId: detail.itemId,
        item: {
          itemId: detail.itemId,
          itemCode: detail.itemCode,
          itemName: detail.itemName,
          itemNameAr: detail.itemNameAr,
          units, // ✅ Include units
        } as Item,
        unitId: detail.unitId,
        quantity: detail.quantity,
        unitPrice: detail.unitPrice,
        notes: detail.notes || '',
      };
    });

    const lines = await Promise.all(linesPromises);
    setDetailLines(lines);

    // ✅ Set payee name from purchaseReturn
    updatePayeeName(
      purchaseReturn.paymentMethod,
      purchaseReturn.supplierName,
      purchaseReturn.supplierNameAr,
      purchaseReturn.custodyName,
      purchaseReturn.custodyNameAr,
      purchaseReturn.paymentAccountName,
      purchaseReturn.paymentAccountNameAr
    );
  };

  // ✅ NEW: Helper function to update payee name
  const updatePayeeName = (
    method: string,
    supplierName?: string,
    supplierNameAr?: string,
    custodyName?: string,
    custodyNameAr?: string,
    accountName?: string,
    accountNameAr?: string
  ) => {
    let name = '';
    switch (method) {
      case 'Supplier':
        name = isRTL ? (supplierNameAr || supplierName || '') : (supplierName || '');
        break;
      case 'Custody':
        name = isRTL ? (custodyNameAr || custodyName || '') : (custodyName || '');
        break;
      case 'PaymentAccount':
        name = isRTL ? (accountNameAr || accountName || '') : (accountName || '');
        break;
    }
    setPayeeName(name);
  };

  const loadNewReturnNumber = async () => {
    try {
      const returnNumber = await purchaseReturnsService.generateReturnNumber();
      setFormData((prev) => ({ ...prev, returnNumber }));
    } catch (error) {
      console.error('Failed to generate return number:', error);
    }
  };

  // ✅ NEW: Load invoice items when invoice is selected
  const loadInvoiceItems = async (invoiceId: number) => {
    try {
      // 1. Load full invoice details first
      const invoice = await purchaseInvoicesService.getById(invoiceId);
      
      // ✅ Determine payment method from invoice data
      let paymentMethod = 'Supplier';
      if (invoice.supplierId) {
        paymentMethod = 'Supplier';
      } else if (invoice.custodyId) {
        paymentMethod = 'Custody';
      } else if (invoice.paymentAccountId) {
        paymentMethod = 'PaymentAccount';
      }
      
      // 2. Set payment method and supplier/custody/paymentAccount from invoice
      setFormData(prev => ({
        ...prev,
        paymentMethod: paymentMethod,
        supplierId: invoice.supplierId || null,
        custodyId: invoice.custodyId || null,
        paymentAccountId: invoice.paymentAccountId || null, // ✅ NEW
        warehouseId: invoice.warehouseId, // ✅ Set warehouse from invoice
      }));

      // 3. Set selected supplier/custody/warehouse
      if (invoice.supplierId) {
        const supplier = suppliers.find(s => s.supplierId === invoice.supplierId);
        setSelectedSupplier(supplier || null);
        setSelectedCustody(null);
      } else if (invoice.custodyId) {
        const custody = custodies.find(c => c.custodyId === invoice.custodyId);
        setSelectedCustody(custody || null);
        setSelectedSupplier(null);
      }

      // ✅ Set selected warehouse
      const warehouse = warehouses.find(w => w.warehouseId === invoice.warehouseId);
      setSelectedWarehouse(warehouse || null);

      // ✅ Update payee name - get names from related objects
      let supplierName = '';
      let supplierNameAr = '';
      let custodyName = '';
      let custodyNameAr = '';
      let accountName = invoice.paymentAccountName || '';
      let accountNameAr = invoice.paymentAccountName || ''; // ✅ No Arabic version available
      
      if (invoice.supplierId) {
        const supplier = suppliers.find(s => s.supplierId === invoice.supplierId);
        if (supplier) {
          supplierName = supplier.supplierName;
          supplierNameAr = supplier.supplierNameAr;
        }
      }
      
      if (invoice.custodyId) {
        const custody = custodies.find(c => c.custodyId === invoice.custodyId);
        if (custody) {
          custodyName = custody.employeeName || '';
          custodyNameAr = custody.employeeNameAr || '';
        }
      }
      
      updatePayeeName(
        paymentMethod,
        supplierName,
        supplierNameAr,
        custodyName,
        custodyNameAr,
        accountName,
        accountNameAr
      );

      // 4. Load invoice items
      const invoiceItems = await purchaseReturnsService.getInvoiceItems(invoiceId);
      
      // 5. Load units for each item
      const linesPromises = invoiceItems.map(async (invItem, index) => {
        // Load units for this item
        let units = await itemUnitsService.getItemUnits(invItem.itemId);
        
        // Check if base unit exists
        const item = items.find(i => i.itemId === invItem.itemId);
        if (item) {
          const hasBaseUnit = units.some(u => u.unitId === item.baseUnitId);
          
          if (!hasBaseUnit && item.baseUnitId) {
            const allUnits = await unitsService.getAll();
            const baseUnit = allUnits.find(u => u.unitId === item.baseUnitId);
            
            if (baseUnit) {
              const baseUnitAsItemUnit = {
                itemUnitId: 0,
                itemId: item.itemId,
                unitId: baseUnit.unitId,
                unitName: baseUnit.unitName,
                unitNameAr: baseUnit.unitNameAr,
                unitCode: baseUnit.unitCode,
                conversionFactor: 1,
                defaultSalePrice: 0,
                defaultPurchasePrice: 0,
                isDefaultSale: true,
                isDefaultPurchase: true,
              };
              units = [baseUnitAsItemUnit, ...units];
            }
          }
        }

        return {
          id: Date.now().toString() + index,
          itemId: invItem.itemId,
          item: {
            itemId: invItem.itemId,
            itemCode: invItem.itemCode,
            itemName: invItem.itemName,
            itemNameAr: invItem.itemNameAr,
            units, // ✅ Include units
          } as Item,
          unitId: invItem.unitId,
          quantity: invItem.availableToReturn, // Default to max available
          unitPrice: invItem.unitPrice,
          notes: '',
        };
      });

      const lines = await Promise.all(linesPromises);
      setDetailLines(lines);
      
      toast.success(language === 'ar' 
        ? `تم تحميل ${invoiceItems.length} صنف من الفاتورة` 
        : `Loaded ${invoiceItems.length} items from invoice`);
    } catch (error: any) {
      console.error('Failed to load invoice items:', error);
      toast.error(error.response?.data?.message || (language === 'ar' ? 'فشل تحميل أصناف الفاتورة' : 'Failed to load invoice items'));
    }
  };

  // ✅ NEW: Watch for invoice selection changes
  useEffect(() => {
    if (formData.purchaseInvoiceId && !purchaseReturn) {
      // Only auto-load for new returns, not when editing
      loadInvoiceItems(formData.purchaseInvoiceId);
    }
  }, [formData.purchaseInvoiceId]);

  const addNewLine = () => {
    const newLine: DetailLine = {
      id: Date.now().toString(),
      itemId: null,
      item: null,
      unitId: null,
      quantity: 1,
      unitPrice: 0,
      notes: '',
    };
    setDetailLines((prev) => [...prev, newLine]);
  };

  const removeLine = (id: string) => {
    setDetailLines((prev) => prev.filter((line) => line.id !== id));
  };

  const updateLine = (id: string, field: keyof DetailLine, value: any) => {
    setDetailLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, [field]: value } : line))
    );
  };

  const handleItemChange = async (lineId: string, item: Item | null) => {
    if (!item) {
      updateLine(lineId, 'item', null);
      updateLine(lineId, 'itemId', null);
      updateLine(lineId, 'unitId', null);
      return;
    }

    try {
      const policy = await itemSuppliersService.getItemPolicy(item.itemId);
      
      // Check if supplier is required for this item
      if (policy.isSupplierRequired && formData.paymentMethod === 'Supplier' && !formData.supplierId) {
        toast.warning(language === 'ar' ? 'هذا الصنف يتطلب اختيار مورد' : 'This item requires supplier selection');
        return;
      }

      // Check approved suppliers for this item
      if (formData.paymentMethod === 'Supplier' && formData.supplierId) {
        const approvedSuppliers = await itemSuppliersService.getSuppliersByItem(item.itemId, true);
        
        if (approvedSuppliers.length > 0) {
          const isApproved = approvedSuppliers.some(s => s.supplierId === formData.supplierId);
          
          if (!isApproved) {
            const supplierNames = approvedSuppliers.map(s => 
              language === 'ar' ? s.supplierNameAr : s.supplierName
            ).join(', ');
            
            toast.error(
              language === 'ar' 
                ? `هذا الصنف متاح فقط من الموردين: ${supplierNames}`
                : `This item is only available from suppliers: ${supplierNames}`
            );
            return;
          }
        }
      }

      // Get units from API
      let units = await itemUnitsService.getItemUnits(item.itemId);
      
      // Check if base unit exists
      const hasBaseUnit = units.some(u => u.unitId === item.baseUnitId);
      
      if (!hasBaseUnit && item.baseUnitId) {
        const allUnits = await unitsService.getAll();
        const baseUnit = allUnits.find(u => u.unitId === item.baseUnitId);
        
        if (baseUnit) {
          const baseUnitAsItemUnit = {
            itemUnitId: 0,
            itemId: item.itemId,
            unitId: baseUnit.unitId,
            unitName: baseUnit.unitName,
            unitNameAr: baseUnit.unitNameAr,
            unitCode: baseUnit.unitCode,
            conversionFactor: 1,
            defaultSalePrice: 0,
            defaultPurchasePrice: 0,
            isDefaultSale: true,
            isDefaultPurchase: true,
          };
          units = [baseUnitAsItemUnit, ...units];
        }
      }

      // Update item with units
      const itemWithUnits = { ...item, units };
      updateLine(lineId, 'item', itemWithUnits);
      updateLine(lineId, 'itemId', item.itemId);
      
      // Set default unit
      if (units.length > 0) {
        updateLine(lineId, 'unitId', units[0].unitId);
      }
    } catch (error) {
      console.error('Error loading item:', error);
      toast.error(language === 'ar' ? 'فشل تحميل بيانات الصنف' : 'Failed to load item data');
    }
  };

  const calculateTotal = () => {
    return detailLines.reduce((sum, line) => {
      if (line.itemId && line.unitId) {
        return sum + (line.quantity * line.unitPrice);
      }
      return sum;
    }, 0);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.paymentMethod) {
      newErrors.paymentMethod = t.paymentSourceRequired;
    }

    if (formData.paymentMethod === 'Supplier' && !formData.supplierId) {
      newErrors.supplierId = t.required;
    }
    if (formData.paymentMethod === 'Custody' && !formData.custodyId) {
      newErrors.custodyId = t.required;
    }

    if (!formData.warehouseId) {
      newErrors.warehouseId = t.required;
    }
    if (detailLines.length === 0) {
      newErrors.details = language === 'ar' ? 'يجب إضافة سطر واحد على الأقل' : 'At least one line is required';
    }
    detailLines.forEach((line, index) => {
      if (!line.itemId) {
        newErrors[`item_${index}`] = t.required;
      }
      if (!line.unitId) {
        newErrors[`unit_${index}`] = t.required;
      }
      if (line.quantity <= 0) {
        newErrors[`quantity_${index}`] = language === 'ar' ? 'يجب أن تكون أكبر من صفر' : 'Must be > 0';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error(language === 'ar' ? 'يرجى تصحيح الأخطاء' : 'Please fix errors');
      return;
    }

    setLoading(true);
    try {
      if (purchaseReturn) {
        const updateDto: UpdatePurchaseReturnDto = {
          purchaseReturnId: purchaseReturn.purchaseReturnId,
          returnDate: formData.returnDate,
          purchaseInvoiceId: formData.purchaseInvoiceId ?? undefined,
          paymentMethod: formData.paymentMethod,
          supplierId: formData.supplierId ?? undefined,
          custodyId: formData.custodyId ?? undefined,
          paymentAccountId: formData.paymentAccountId ?? undefined, // ✅ NEW
          warehouseId: formData.warehouseId!,
          returnReason: formData.returnReason || undefined,
          notes: formData.notes || undefined,
          details: detailLines.map((line, index) => ({
            returnDetailId: (() => { const p = parseInt(line.id); return !isNaN(p) && p > 0 && p < 2147483647 ? p : 0; })(),
            lineNumber: index + 1,
            itemId: line.itemId!,
            unitId: line.unitId!,
            quantity: line.quantity,
            unitPrice: line.unitPrice,
            notes: line.notes || undefined,
          })),
        };
        await purchaseReturnsService.update(purchaseReturn.purchaseReturnId, updateDto);
        toast.success(t.updated);
      } else {
        const createDto: CreatePurchaseReturnDto = {
          returnNumber: formData.returnNumber,
          returnDate: formData.returnDate,
          purchaseInvoiceId: formData.purchaseInvoiceId ?? undefined,
          paymentMethod: formData.paymentMethod,
          supplierId: formData.supplierId ?? undefined,
          custodyId: formData.custodyId ?? undefined,
          paymentAccountId: formData.paymentAccountId ?? undefined, // ✅ NEW
          warehouseId: formData.warehouseId!,
          returnReason: formData.returnReason || undefined,
          notes: formData.notes || undefined,
          details: detailLines.map((line, index) => ({
            lineNumber: index + 1,
            itemId: line.itemId!,
            unitId: line.unitId!,
            quantity: line.quantity,
            unitPrice: line.unitPrice,
            notes: line.notes || undefined,
          })),
        };
        await purchaseReturnsService.create(createDto);
        toast.success(t.saved);
      }
      onSave();
      handleClose();
    } catch (error: any) {
      console.error('Failed to save purchase return:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      toast.error(error.response?.data?.message || (language === 'ar' ? 'فشل الحفظ' : 'Save failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      returnNumber: '',
      returnDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'Supplier',
      purchaseInvoiceId: null,
      supplierId: null,
      custodyId: null,
      paymentAccountId: null, // ✅ NEW
      warehouseId: null,
      returnReason: '',
      notes: '',
    });
    setDetailLines([]);
    setSelectedSupplier(null);
    setSelectedCustody(null);
    setSelectedWarehouse(null);
    setSelectedInvoice(null);
    setPayeeName(''); // ✅ NEW
    setErrors({});
    onClose();
  };

  const handlePaymentMethodChange = (method: string) => {
    setFormData({
      ...formData,
      paymentMethod: method,
      supplierId: null,
      custodyId: null,
    });
    setSelectedSupplier(null);
    setSelectedCustody(null);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="xl" 
      fullWidth 
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{ 
        sx: { 
          borderRadius: 3, 
          bgcolor: isDarkMode ? '#1a1a2e' : 'white', 
          minHeight: '92vh',
          backgroundImage: isDarkMode 
            ? 'linear-gradient(rgba(255,255,255,0.02), rgba(255,255,255,0.02))'
            : 'linear-gradient(rgba(102, 126, 234, 0.02), rgba(118, 75, 162, 0.02))',
        } 
      }}
      TransitionComponent={Fade}
      transitionDuration={300}
    >
      <DialogTitle 
        sx={{ 
          background: isDarkMode 
            ? 'linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%)'
            : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
          color: 'white', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          py: 1.5,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box 
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              p: 1, 
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ReturnIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {purchaseReturn ? t.editPurchaseReturn : t.addPurchaseReturn}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.7rem' }}>
              {formData.returnNumber}
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={handleClose} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2, px: 3 }}>
        {/* Basic Information */}
        <Zoom in timeout={300}>
          <Card 
            sx={{ 
              mb: 2.5, 
              borderRadius: 3,
              background: isDarkMode 
                ? 'linear-gradient(135deg, rgba(30, 58, 95, 0.3) 0%, rgba(44, 82, 130, 0.3) 100%)'
                : 'linear-gradient(135deg, rgba(240, 147, 251, 0.1) 0%, rgba(245, 87, 108, 0.1) 100%)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
                <ReceiptIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {t.basicInfo}
                </Typography>
              </Stack>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2.5 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField 
                    fullWidth 
                    label={t.returnNumber} 
                    value={formData.returnNumber} 
                    disabled 
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ReceiptIcon fontSize="small" color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {!purchaseReturn && (
                    <IconButton 
                      color="primary" 
                      onClick={loadNewReturnNumber} 
                      sx={{ 
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  )}
                </Box>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t.returnDate}
                    value={formData.returnDate ? new Date(formData.returnDate) : null}
                    onChange={(date) => setFormData({ ...formData, returnDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                    format="dd/MM/yyyy"
                    views={['year', 'month', 'day']}
                    openTo="day"
                    minDate={minDate}
                    maxDate={maxDate}
                    disabled={dateLocked}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </LocalizationProvider>

                <Autocomplete 
                  fullWidth 
                  options={warehouses} 
                  getOptionLabel={(option) => `${option.warehouseCode} - ${isRTL ? option.warehouseNameAr : option.warehouseName}`}
                  value={selectedWarehouse} 
                  onChange={(_, newValue) => { 
                    setSelectedWarehouse(newValue); 
                    setFormData({ ...formData, warehouseId: newValue?.warehouseId || null }); 
                  }} 
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label={`${t.warehouse} *`} 
                      error={!!errors.warehouseId} 
                      helperText={errors.warehouseId} 
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <WarehouseIcon fontSize="small" color="primary" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )} 
                />

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'start' }}>
                  <Box sx={{ flex: 1 }}> {/* ✅ Give Autocomplete more space */}
                    <Autocomplete 
                      fullWidth 
                      options={purchaseInvoices} 
                      getOptionLabel={(option) => {
                        // ✅ Show invoice number with payee name
                        let payeeName = '';
                        
                        // Get supplier name
                        if (option.supplierId && option.supplierName) {
                          const supplierName = isRTL ? (option.supplierNameAr || option.supplierName) : option.supplierName;
                          payeeName = supplierName || '';
                        } 
                        // Get custody name from custodies array
                        else if (option.custodyId) {
                          const custody = custodies.find(c => c.custodyId === option.custodyId);
                          if (custody) {
                            payeeName = isRTL ? (custody.employeeNameAr || custody.employeeName || '') : (custody.employeeName || '');
                          }
                        } 
                        // Get payment account name
                        else if (option.paymentAccountId && option.paymentAccountName) {
                          payeeName = option.paymentAccountName;
                        }
                        
                        return payeeName ? `${option.invoiceNumber} - ${payeeName}` : option.invoiceNumber;
                      }} 
                      value={selectedInvoice} 
                      onChange={(_, newValue) => { 
                        setSelectedInvoice(newValue); 
                        setFormData({ ...formData, purchaseInvoiceId: newValue?.purchaseInvoiceId || null }); 
                      }} 
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label={t.selectInvoice} 
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <ReceiptIcon fontSize="small" color="action" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )} 
                    />
                  </Box>
                  
                  <Button
                    variant="outlined"
                    size="small" // ✅ Changed from "medium" to "small"
                    onClick={() => formData.purchaseInvoiceId && loadInvoiceItems(formData.purchaseInvoiceId)}
                    disabled={!formData.purchaseInvoiceId}
                    sx={{ minWidth: 80, px: 1.5 }} // ✅ Reduced from 120 to 80, removed height
                    startIcon={<RefreshIcon fontSize="small" />}
                  >
                    {language === 'ar' ? 'تحميل' : 'Load'}
                  </Button>
                </Box>

                <Box sx={{ gridColumn: { xs: '1', md: 'span 4' } }}>
                  <TextField 
                    fullWidth 
                    label={`${t.returnReason} (${t.optional})`} 
                    value={formData.returnReason} 
                    onChange={(e) => setFormData({ ...formData, returnReason: e.target.value })} 
                    multiline 
                    rows={2} 
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <NotesIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Zoom>

        {/* Payment Information */}
        <Zoom in timeout={400}>
          <Card 
            sx={{ 
              mb: 2.5, 
              borderRadius: 3,
              background: isDarkMode 
                ? 'linear-gradient(135deg, rgba(30, 58, 95, 0.3) 0%, rgba(44, 82, 130, 0.3) 100%)'
                : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
                <PaymentIcon sx={{ color: 'secondary.main', fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold" color="secondary.main">
                  {t.paymentInfo}
                </Typography>
              </Stack>

              <FormControl component="fieldset" fullWidth error={!!errors.paymentMethod} sx={{ mb: 2.5 }}>
                <FormLabel component="legend" sx={{ mb: 1.5, fontWeight: 600 }}>
                  {t.paymentMethod} *
                </FormLabel>
                <RadioGroup
                  row
                  value={formData.paymentMethod}
                  onChange={(e) => handlePaymentMethodChange(e.target.value)}
                  sx={{ gap: 2 }}
                >
                  <FormControlLabel
                    value="Supplier"
                    control={<Radio />}
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <BusinessIcon color="primary" />
                        <Typography>{t.methodSupplier}</Typography>
                      </Stack>
                    }
                    sx={{
                      border: `2px solid ${formData.paymentMethod === 'Supplier' ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.2)}`,
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      m: 0,
                      bgcolor: formData.paymentMethod === 'Supplier' ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                      transition: 'all 0.3s',
                    }}
                  />
                  <FormControlLabel
                    value="Custody"
                    control={<Radio />}
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon color="secondary" />
                        <Typography>{t.methodCustody}</Typography>
                      </Stack>
                    }
                    sx={{
                      border: `2px solid ${formData.paymentMethod === 'Custody' ? theme.palette.secondary.main : alpha(theme.palette.secondary.main, 0.2)}`,
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      m: 0,
                      bgcolor: formData.paymentMethod === 'Custody' ? alpha(theme.palette.secondary.main, 0.05) : 'transparent',
                      transition: 'all 0.3s',
                    }}
                  />
                </RadioGroup>
              </FormControl>

              {formData.paymentMethod === 'Supplier' && (
                <Fade in timeout={300}>
                  <Box>
                    <Autocomplete 
                      fullWidth 
                      options={suppliers} 
                      getOptionLabel={(option) => `${option.supplierCode} - ${isRTL ? option.supplierNameAr : option.supplierName}`}
                      value={selectedSupplier} 
                      onChange={(_, newValue) => { 
                        setSelectedSupplier(newValue); 
                        setFormData({ ...formData, supplierId: newValue?.supplierId || null }); 
                      }} 
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label={`${t.supplier} *`} 
                          error={!!errors.supplierId} 
                          helperText={errors.supplierId} 
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <BusinessIcon fontSize="small" color="primary" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )} 
                    />
                  </Box>
                </Fade>
              )}

              {formData.paymentMethod === 'Custody' && (
                <Fade in timeout={300}>
                  <Box>
                    <Autocomplete 
                      fullWidth 
                      options={custodies} 
                      getOptionLabel={(option) => `${option.custodyNumber} - ${isRTL ? (option.employeeNameAr || '') : (option.employeeName || '')}`}
                      value={selectedCustody} 
                      onChange={(_, newValue) => { 
                        setSelectedCustody(newValue); 
                        setFormData({ ...formData, custodyId: newValue?.custodyId || null }); 
                      }} 
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label={`${t.custody} *`} 
                          error={!!errors.custodyId} 
                          helperText={errors.custodyId} 
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <PersonIcon fontSize="small" color="secondary" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )} 
                    />
                  </Box>
                </Fade>
              )}

              {/* ✅ NEW: Display Payee Name when invoice is selected */}
              {payeeName && formData.purchaseInvoiceId && (
                <Fade in timeout={400}>
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      label={t.payee}
                      value={payeeName}
                      disabled
                      size="small"
                      sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: 'rgba(102, 126, 234, 0.95)',
                          fontWeight: 700,
                          fontSize: '0.95rem',
                        },
                        '& .MuiInputLabel-root.Mui-disabled': {
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        },
                        '& .MuiOutlinedInput-root.Mui-disabled': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          '& fieldset': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 2,
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Chip
                              label={
                                formData.paymentMethod === 'Supplier' ? t.methodSupplier :
                                formData.paymentMethod === 'Custody' ? t.methodCustody :
                                t.methodPaymentAccount
                              }
                              size="small"
                              color="primary"
                              sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Fade>
              )}
            </CardContent>
          </Card>
        </Zoom>

        {/* Return Details */}
        <Zoom in timeout={500}>
          <Card 
            sx={{ 
              mb: 2, 
              borderRadius: 3,
              background: isDarkMode 
                ? 'linear-gradient(135deg, rgba(30, 58, 95, 0.3) 0%, rgba(44, 82, 130, 0.3) 100%)'
                : 'linear-gradient(135deg, rgba(255, 193, 7, 0.05) 0%, rgba(255, 152, 0, 0.05) 100%)',
              border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
                <InventoryIcon sx={{ color: 'warning.main', fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold" color="warning.main">
                  {t.returnDetails}
                </Typography>
              </Stack>

              <Box ref={gridRef}>
              <TableContainer
                component={Paper}
                sx={{
                  mb: 2.5,
                  maxHeight: 450,
                  borderRadius: 2,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 250, bgcolor: alpha(theme.palette.primary.main, 0.1), fontWeight: 700 }}>{t.item}</TableCell>
                      <TableCell sx={{ minWidth: 120, bgcolor: alpha(theme.palette.primary.main, 0.1), fontWeight: 700 }}>{t.unit}</TableCell>
                      <TableCell sx={{ minWidth: 100, bgcolor: alpha(theme.palette.primary.main, 0.1), fontWeight: 700 }}>{t.quantity}</TableCell>
                      <TableCell sx={{ minWidth: 120, bgcolor: alpha(theme.palette.primary.main, 0.1), fontWeight: 700 }}>{t.unitPrice}</TableCell>
                      <TableCell sx={{ minWidth: 120, bgcolor: alpha(theme.palette.primary.main, 0.1), fontWeight: 700 }}>{t.totalPrice}</TableCell>
                      <TableCell sx={{ minWidth: 200, bgcolor: alpha(theme.palette.primary.main, 0.1), fontWeight: 700 }}>{t.lineNotes}</TableCell>
                      <TableCell sx={{ width: 60, bgcolor: alpha(theme.palette.primary.main, 0.1) }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailLines.map((line, index) => (
                      <TableRow 
                        key={line.id}
                        sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                          },
                        }}
                      >
                        <TableCell>
                          <Autocomplete 
                            options={items} 
                            getOptionLabel={(option) => `${option.itemCode} - ${isRTL ? option.itemNameAr : option.itemName}`} 
                            value={line.item} 
                            onChange={(_, newValue) => handleItemChange(line.id, newValue)}
                            renderInput={(params) => <TextField {...params} size="small" error={!!errors[`item_${index}`]} />} 
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete 
                            options={line.item?.units || []} 
                            getOptionLabel={(option) => isRTL ? option.unitNameAr : option.unitName} 
                            value={line.item?.units?.find(u => u.unitId === line.unitId) || null} 
                            onChange={(_, newValue) => updateLine(line.id, 'unitId', newValue?.unitId || null)} 
                            renderInput={(params) => <TextField {...params} size="small" error={!!errors[`unit_${index}`]} />} 
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={line.quantity === 0 ? '' : String(line.quantity)}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                updateLine(line.id, 'quantity', v === '' || v === '.' ? 0 : parseFloat(v));
                              }
                            }}
                            error={!!errors[`quantity_${index}`]}
                            inputProps={{ inputMode: 'decimal' }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={line.unitPrice === 0 ? '' : String(line.unitPrice)}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                updateLine(line.id, 'unitPrice', v === '' || v === '.' ? 0 : parseFloat(v));
                              }
                            }}
                            inputProps={{ inputMode: 'decimal' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={(line.quantity * line.unitPrice).toFixed(3)} 
                            color="success" 
                            size="small"
                            sx={{ fontWeight: 700, minWidth: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField 
                            size="small" 
                            fullWidth 
                            value={line.notes} 
                            onChange={(e) => updateLine(line.id, 'notes', e.target.value)} 
                            placeholder={t.optional}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => removeLine(line.id)}
                            sx={{
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </Box>

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addNewLine}
                  variant="outlined"
                  size="large"
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                    },
                  }}
                >
                  {t.addLine}
                </Button>
                <Chip 
                  icon={<CheckIcon />}
                  label={`${t.totalAmount}: ${calculateTotal().toFixed(3)} KD`}
                  color="primary" 
                  sx={{ 
                    fontSize: '1.1rem', 
                    fontWeight: 'bold', 
                    px: 3, 
                    py: 3,
                    borderRadius: 2,
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                  }} 
                />
              </Stack>

              <TextField 
                fullWidth 
                label={`${t.notes} (${t.optional})`} 
                value={formData.notes} 
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })} 
                multiline 
                rows={3} 
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <NotesIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </CardContent>
          </Card>
        </Zoom>
      </DialogContent>

      <DialogActions 
        sx={{ 
          p: 2, 
          gap: 2,
          bgcolor: isDarkMode ? alpha('#000', 0.2) : alpha('#f5f5f5', 0.8),
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Button 
          onClick={handleClose} 
          disabled={loading} 
          size="large"
          variant="outlined"
          sx={{
            px: 4,
            py: 1,
            borderRadius: 2,
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2,
            },
          }}
        >
          {t.cancel}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading} 
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
          size="large" 
          sx={{ 
            px: 5,
            py: 1,
            borderRadius: 2,
            background: 'linear-gradient(45deg, #f093fb 30%, #f5576c 90%)', 
            boxShadow: '0 4px 15px rgba(240, 147, 251, 0.4)',
            fontSize: '1rem',
            fontWeight: 700,
            '&:hover': { 
              background: 'linear-gradient(45deg, #e082ea 30%, #e4465b 90%)',
              boxShadow: '0 6px 20px rgba(240, 147, 251, 0.5)',
              transform: 'translateY(-2px)',
            },
            '&:disabled': {
              background: alpha(theme.palette.action.disabledBackground, 0.5),
            },
            transition: 'all 0.3s',
          }}
        >
          {t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseReturnDialog;